<template>
    <div
        v-touch:swipe.left="previousPage"
        v-touch:swipe.right="nextPage"
        class="gallery gallery-controls
         w-full flex flex-col items-center align-center phone:mb-12 mb-6 tablet:px-12"
    >
        <div class="flex align-center items-center mx-12">
            <product-gallery-paginator
                v-if="showPreviousPageButton"
                direction="previous"
                :handle-pagination="previousPage"
                class="absolute smallPhone:hidden mt-8"
            />

            <div class="gallery-thumbnails flex items-center overflow-hidden">
                <ul
                    class="flex transition-all duration-500 ease-in-out flex items-center"
                    :style="currentTranslateXStyle"
                >
                    <product-gallery-thumbnail
                        v-for="(thumbItem) in slides"
                        :key="thumbItem.index"
                        :alt="thumbItem.alt"
                        :is-active="thumbItem.index === currentSlideIndex"
                        :thumbnail-url="thumbItem.image_thumbnail_url"
                        @click="thumbnailClicked(thumbItem.index)"
                    />
                </ul>
            </div>

            <product-gallery-paginator
                direction="next"
                :handle-pagination="nextPage"
                class="absolute smallPhone:hidden mt-8"
            />
        </div>


        <!--Mobile Instructions-->
        <p
            class="hidden smallPhone:block w-full mt-4 text-center font-bold uppercase text-white cursor-pointer"
            @click="nextPage"
            v-text="swipeInstructions" />
    </div>
</template>

<script>
import ProductGalleryPaginator from '@web/components/ProductGalleries/controls/Paginator';
import ProductGalleryThumbnail from '@web/components/ProductGalleries/controls/Thumbnail';

export default {
  name: 'ProductGalleryThumbnailControls',
  components: {
    ProductGalleryThumbnail,
    ProductGalleryPaginator,
  },
  props: {
    slides: {
      type: Object,
      required: true,
    },
    currentSlideIndex: {
      type: Number,
      required: true,
    },
    swipeInstructions: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentWindowWidth: window.innerWidth,
      currentThumbIndex: this.currentSlideIndex, // this holds the current thumb that is first in the currently displayed page
      thumbnailWidth: 104, // includes the space between
    };
  },
  computed: {
    numberOfThumbsPerPage() {
      if (this.currentWindowWidth < 768) {
        return 3;
      }

      // at iPad portrait, there's enough space to show 5
      if (this.currentWindowWidth >= 768 && this.currentWindowWidth < 1024) {
        return 5;
      }

      // however at iPad landscape we return to 3
      // because the product description area ends up to the right
      // which shrinks the available space for the thumbnails
      if (this.currentWindowWidth >= 1024 && this.currentWindowWidth < 1279) {
        return 3;
      }

      // back to 5 as soon as we have space for it
      if (this.currentWindowWidth >= 1280 && this.currentWindowWidth < 1920) {
        return 5;
      }

      if (this.currentWindowWidth > 1920) {
        return 8;
      }
    },
    currentlyVisibleMaxIndex() {
      // this gives the "last" currently visible thumbnail
      // -1 because index start at zero
      return this.currentThumbIndex + this.numberOfThumbsPerPage - 1;
    },
    showPreviousPageButton() {
      return this.currentThumbIndex > 0;
    },
    currentPosition() {
      // the current thumbnail that's in the 1st position
      // multiplied by the width of each thumb
      // gives us the current translateX value
      return this.currentThumbIndex * this.thumbnailWidth;
    },
    currentTranslateXStyle() {
      return `transform: translateX(-${this.currentPosition}px)`;
    },
    totalSlides() {
      return Object.keys(this.slides).length;
    },
  },
  watch: {
    currentSlideIndex(newCurrentSlideIndex) {
      // if the slide that's being put into view now
      // is greater than the current "last" thumbnail in view
      // then set the thumbnail carousel to display the current slide
      // as the first one in the carousel
      if (newCurrentSlideIndex > this.currentlyVisibleMaxIndex) {
        this.currentThumbIndex = newCurrentSlideIndex;
      }

      // likewise, if it's smaller than the current "first" thumb in view
      // then set the thumbnail carousel to display the current slide
      // as the first one in the carousel
      if (this.currentThumbIndex > newCurrentSlideIndex) {
        this.currentThumbIndex = newCurrentSlideIndex;
      }
    },
  },
  created() {
    window.addEventListener('resize', this.handleWindowResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    handleWindowResize() {
      this.currentWindowWidth = window.innerWidth;
      this.currentThumbIndex = this.currentSlideIndex;
    },
    previousPage() {
      // prevent going lower than 0
      this.currentThumbIndex = Math.max(
        this.currentThumbIndex - this.numberOfThumbsPerPage,
        0,
      );
    },
    nextPage() {
      let nextIndex = this.currentThumbIndex + this.numberOfThumbsPerPage;

      // go back to first slide if pagination is over
      if (nextIndex > this.totalSlides) {
        nextIndex = 0;
      }

      this.currentThumbIndex = nextIndex;
    },
    thumbnailClicked(thumbIndex) {
      this.$emit('thumbnailClicked', thumbIndex);
    },
  },
};
</script>

<style scoped>

</style>
